import { Injectable } from '@angular/core'
import { initializeApp } from '@angular/fire/app'

import { AngularFireMessaging } from '@angular/fire/compat/messaging'
import { getMessaging, getToken } from '@angular/fire/messaging'
import { Router } from '@angular/router'
import { gql } from 'apollo-angular'
import { catchError, EMPTY, from, Observable, of, Subject, tap } from 'rxjs'
import { NotificationService } from '../../providers/notification/notification.service'
import { DataService } from './data.service'

const SAVE_FCM_TOKEN = gql`
  mutation SaveFcmToken($input: SaveLoginMetadataInput!) {
    saveLoginMetadata(input: $input)
  }
`

const firebaseConfig = {
  apiKey: 'AIzaSyBwUKjVvDS66F6pApIpSw-ilSw9uLl8Qyg',
  authDomain: 'galaxy-pynt-3f50b.firebaseapp.com',
  projectId: 'galaxy-pynt-3f50b',
  storageBucket: 'galaxy-pynt-3f50b.firebasestorage.app',
  messagingSenderId: '480774253394',
  appId: '1:480774253394:web:39f7109f1474af52d6a88b',
  measurementId: 'G-NN5HQNJ21R',
  vapidKey:
    'BOJyTFWx9Y6s489dlxZjOToTb-ihB4Aim2IuT3NyX3EXXqlwGMFRzcNTp2IxZpSKTac3SJ69k-Lb2_BLBUey5Zk'
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private fcmToken: string | null = null
  private activeConversationId: string | null = null
  private messaging
  private messageReceived = new Subject<any>()
  private chatUpdated = new Subject<string>()

  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private notifcationService: NotificationService,
    private dataService: DataService,
    private router: Router
  ) {
    const app = initializeApp(firebaseConfig)
    this.messaging = getMessaging(app)
  }

  get isPushNotificationSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      window.isSecureContext
    )
  }

  get hasPushNotificationPermission(): boolean {
    return (
      this.isPushNotificationSupported && Notification.permission === 'granted'
    )
  }

  saveFcmToken(token: string): Observable<any> {
    const observable = this.dataService.mutate(SAVE_FCM_TOKEN, {
      input: {
        fcmWebToken: token
      }
    })

    observable.subscribe({
      next: (response: any) => {
        if (response['saveLoginMetadata']) {
          console.log('FCM token saved successfully:', response)
        } else {
          console.error('Failed to save FCM token')
        }
      },
      error: (error) => {
        console.error('Error saving FCM token:', error)
      }
    })

    return observable
  }

  listenForMessages(): Observable<any> {
    if (!this.hasPushNotificationPermission) {
      return EMPTY
    }

    return this.angularFireMessaging.messages.pipe(
      tap((payload) => {
        this.messageReceived.next(payload)
      }),
      catchError((error) => {
        console.error('Error listening for messages:', error)
        return of(null)
      })
    )
  }

  async requestPermission(): Promise<boolean> {
    if (!this.isPushNotificationSupported) {
      return false
    }

    if (this.fcmToken) {
      return true
    }

    const permission =
      Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission

    if (permission !== 'granted') {
      return false
    }

    try {
      const sw = await navigator.serviceWorker.register(
        '/admin/firebase-messaging-sw.js',
        {
          scope: '/admin/'
        }
      )

      const currentToken = await getToken(this.messaging, {
        serviceWorkerRegistration: sw,
        vapidKey: firebaseConfig.vapidKey
      })

      if (!currentToken) {
        console.log(
          'No registration token available. Request permission to generate one.'
        )
        return false
      }

      this.fcmToken = currentToken
      this.saveFcmToken(currentToken)
      return true
    } catch (err) {
      console.log('An error occurred while retrieving token.', err)
      return false
    }
  }

  /**
   * Get FCM token - this is all we need if we're not implementing push notifications
   */
  getCurrentToken(): string | null {
    return this.fcmToken
  }

  /**
   * Delete FCM token (for logout)
   */
  deleteToken(): Observable<boolean> {
    if (!this.fcmToken) {
      return of(true)
    }

    return from(this.angularFireMessaging.deleteToken(this.fcmToken)).pipe(
      tap(() => {
        this.fcmToken = null
        console.log('FCM token deleted')
      }),
      catchError((error) => {
        console.error('Error deleting token:', error)
        return of(false)
      })
    )
  }

  setActiveConversation(conversationId: string | null) {
    this.activeConversationId = conversationId
  }

  isConversationActive(conversationId: string): boolean {
    return this.activeConversationId === conversationId
  }

  /**
   * Get observable for message received events
   */
  get onMessageReceived(): Observable<any> {
    return this.messageReceived.asObservable()
  }

  /**
   * Get observable for active chat updates
   */
  get onChatUpdated(): Observable<string> {
    return this.chatUpdated.asObservable()
  }
}
